import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import {
  OAuthService,
  OAuthResourceServerErrorHandler,
  OAuthModuleConfig,
} from 'angular-oauth2-oidc';
import { Observable, catchError, from, of, switchMap } from 'rxjs';

export class TokenAuthInterceptor implements HttpInterceptor {

  private refreshInProgress: Promise<boolean> | null = null;

  constructor(
    private readonly oAuthService: OAuthService,
    private readonly errorHandler: OAuthResourceServerErrorHandler,
    private readonly moduleConfig: OAuthModuleConfig,
    private readonly useIdToken: boolean
  ) {}

  public intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.moduleConfig?.resourceServer || !this.isAllowedUrl(req.url)) {
      return next.handle(req);
    }

    return this.ensureValidToken().pipe(
      switchMap((hasValidToken) =>
        next.handle(hasValidToken ? this.attachToken(req) : req)
      ),
      catchError((err) => this.errorHandler.handleError(err))
    );
  }

  private ensureValidToken(): Observable<boolean> {
    if (this.isValidToken()) {
      return of(true);
    }

    this.refreshInProgress ??= this.refreshTokens()
      .then(() => true)
      .catch(() => false)
      .finally(() => (this.refreshInProgress = null));

    return from(this.refreshInProgress);
  }

  private refreshTokens(): Promise<unknown> {
    return this.oAuthService.getRefreshToken()
      ? this.oAuthService.refreshToken()
      : this.oAuthService.silentRefresh();
  }

  private isValidToken(): boolean {
    return this.useIdToken
      ? this.oAuthService.hasValidIdToken()
      : this.oAuthService.hasValidAccessToken();
  }

  private attachToken(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.useIdToken
      ? this.oAuthService.getIdToken()
      : this.oAuthService.getAccessToken();

    return req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  private isAllowedUrl(url: string): boolean {
    const allowedUrls = this.moduleConfig?.resourceServer?.allowedUrls;
    if (!allowedUrls) {
      return true;
    }

    const lowerUrl = url.toLowerCase();
    return allowedUrls.some((u) => lowerUrl.startsWith(u.toLowerCase()));
  }
}
